import { useState, useEffect } from 'react';
import {
  adjustAgingBucketsForMaxDaysOverdue,
  mergeNinetyPlusAgingBuckets,
  OUTSTANDING_MAX_DAYS_OVERDUE,
  extractSummary,
} from '../utils/outstandingUtils';
import {
  computeDsoDays,
  trailing12MonthInvoicedUsd,
  type GlobalInvoicedTrendRow,
} from '../utils/dsoUtils';

export interface AgingBucket {
  aging_bucket: string;
  collection_risk: string;
  bucket_display_name: string;
  bucket_description: string;
  date_duration: string;
  invoice_count: number;
  unique_clients: number;
  total_outstanding: number;
  avg_outstanding: number;
  min_days_overdue: number;
  max_days_overdue: number;
  avg_days_overdue: number;
  total_original_amount: number;
  total_paid_amount: number;
  avg_priority_score: number;
  active_clients: number;
  inactive_clients: number;
  enterprise_outstanding: number;
  strategic_outstanding: number;
  standard_outstanding: number;
  small_outstanding: number;
  percentage_of_total: number;
  outstanding_ratio: number;
  collection_rate: number;
  action_priority: string;
  // Summary fields (from first bucket row)
  grand_total_outstanding: number;
  total_invoices: number;
  total_unique_clients: number;
  critical_outstanding: number;
  high_risk_outstanding: number;
  medium_risk_outstanding: number;
  low_risk_outstanding: number;
  current_outstanding: number;
  overdue_outstanding: number;
  overall_overdue_pct: number;
  critical_risk_pct: number;
  collections_alert: string;
  clients_list: string; // JSON string
  top_clients_overall_list?: string; // JSON string, first row only
}

export interface BucketClient {
  name: string;
  outstanding: number;
  tier: string;
  status: string;
  invoice_count: number;
  days_overdue: number;
  paid_12m_count: number;
  paid_12m_amount: number;
  last_invoice: string;
}

export interface OverallClient {
  name: string;
  outstanding: number;
  tier: string;
  status: string;
  invoice_count: number;
  max_days_overdue: number;
  aging_buckets: string;
}

export interface TopClientOutstanding {
  contact_id: number;
  contact_name: string;
  client_tier: string;
  status: string;
  payment_reliability: string;
  total_outstanding: number;
  outstanding_invoice_count: number;
  avg_outstanding_per_invoice: number;
  oldest_due_date: string;
  newest_due_date: string;
  avg_days_overdue: number;
  max_days_overdue: number;
  outstanding_90_plus: number;
  outstanding_31_90: number;
  outstanding_1_30: number;
  total_invoiced_amount: number;
  collection_rate_percent: number;
  outstanding_rank: number;
  critical_rank: number;
  overdue_rank: number;
  pct_of_org_outstanding: number;
  historical_on_time_rate: number;
  historical_avg_payment_days: number;
  total_historical_invoices: number;
  total_deposit_amount: number;
  deposit_invoice_count: number;
  total_deposit_adjustment: number;
  deposit_adjustment_line_count: number;
  net_deposit_balance: number;
  collection_risk_category: string;
  recommended_action: string;
  collection_priority_score: number;
}

/** DSO = AR ÷ T12 invoiced × 365 (days). */
export interface DsoMetrics {
  /** Null if T12 sales missing or zero. */
  days: number | null;
  /** Sum of global invoiced USD for latest 12 distinct months. */
  t12InvoicedUsd: number;
}

interface UseOutstandingCollectionsResult {
  agingBuckets: AgingBucket[];
  topClients: TopClientOutstanding[];
  dso: DsoMetrics | null;
  loading: boolean;
  error: string | null;
}

export function useOutstandingCollections(): UseOutstandingCollectionsResult {
  const [agingBuckets, setAgingBuckets] = useState<AgingBucket[]>([]);
  const [topClients, setTopClients] = useState<TopClientOutstanding[]>([]);
  const [dso, setDso] = useState<DsoMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/json-data/outstanding_aging.json').then(r => { if (!r.ok) throw new Error(`Aging HTTP ${r.status}`); return r.json(); }),
      fetch('/json-data/top_clients_outstanding.json').then(r => { if (!r.ok) throw new Error(`Top clients HTTP ${r.status}`); return r.json(); }),
      fetch('/json-data/global_invoiced_trends.json').then(r =>
        r.ok ? r.json() : Promise.resolve([] as GlobalInvoicedTrendRow[]),
      ).catch(() => [] as GlobalInvoicedTrendRow[]),
    ])
      .then(([agingRaw, clientsRaw, trendsRaw]: [any[], any[], GlobalInvoicedTrendRow[]]) => {
        const coercedAging: AgingBucket[] = agingRaw.map(b => ({
          ...b,
          total_outstanding: Number(b.total_outstanding ?? 0),
          total_original_amount: Number(b.total_original_amount ?? 0),
          total_paid_amount: Number(b.total_paid_amount ?? 0),
          enterprise_outstanding: Number(b.enterprise_outstanding ?? 0),
          strategic_outstanding: Number(b.strategic_outstanding ?? 0),
          standard_outstanding: Number(b.standard_outstanding ?? 0),
          small_outstanding: Number(b.small_outstanding ?? 0),
          grand_total_outstanding: Number(b.grand_total_outstanding ?? 0),
          critical_outstanding: Number(b.critical_outstanding ?? 0),
          high_risk_outstanding: Number(b.high_risk_outstanding ?? 0),
          medium_risk_outstanding: Number(b.medium_risk_outstanding ?? 0),
          low_risk_outstanding: Number(b.low_risk_outstanding ?? 0),
          current_outstanding: Number(b.current_outstanding ?? 0),
          overdue_outstanding: Number(b.overdue_outstanding ?? 0),
        }));

        const coercedClients: TopClientOutstanding[] = clientsRaw
          .filter(
            c => (Number(c.max_days_overdue) || 0) <= OUTSTANDING_MAX_DAYS_OVERDUE
          )
          .map(c => ({
            ...c,
            total_outstanding: Number(c.total_outstanding ?? 0),
            outstanding_90_plus: Number(c.outstanding_90_plus ?? 0),
            outstanding_31_90: Number(c.outstanding_31_90 ?? 0),
            outstanding_1_30: Number(c.outstanding_1_30 ?? 0),
            total_invoiced_amount: Number(c.total_invoiced_amount ?? 0),
            total_deposit_amount: Number(c.total_deposit_amount ?? 0),
            total_deposit_adjustment: Number(c.total_deposit_adjustment ?? 0),
            net_deposit_balance: Number(c.net_deposit_balance ?? 0),
          }));

        const merged = mergeNinetyPlusAgingBuckets(
          adjustAgingBucketsForMaxDaysOverdue(coercedAging),
        );
        setAgingBuckets(merged);
        setTopClients(coercedClients);

        const arUsd = extractSummary(merged).grandTotal;
        const t12 = Array.isArray(trendsRaw) ? trailing12MonthInvoicedUsd(trendsRaw) : 0;
        setDso({
          days: computeDsoDays(arUsd, t12),
          t12InvoicedUsd: t12,
        });
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return { agingBuckets, topClients, dso, loading, error };
}
